/**
 * Command K — the Escape ladder, as one pure function.
 *
 * Escape means different things at different depths, and getting the order
 * wrong is how a palette starts feeling like a trap. The previous build needed
 * four separate exit affordances for its AI mode; that count is the tell.
 *
 * The rule: Escape always walks DOWN one rung, never dumps the user out. The
 * only way to close from depth is to press it repeatedly, and every press
 * visibly undoes something.
 *
 * AI CONTEXT
 *
 * This is a pure function over a snapshot rather than a method that reads
 * signals, for two reasons. It keeps `CmdkStateService` free of a dependency
 * on the flow stack, the ask service and the voice service (which would
 * otherwise form a cycle, since each of those wants to read state). And it
 * makes the precedence a truth table a spec walks exhaustively, which is the
 * only way this stays correct as rungs are added.
 *
 * There is still exactly ONE place that knows the order: this file.
 */

/** Everything the ladder depends on, read at the moment Escape is pressed. */
export interface CmdkEscapeSnapshot {
  /** Voice dictation is running. */
  readonly listening: boolean;
  /** The action-menu popover is open. */
  readonly actionMenuOpen: boolean;
  /** How many flow frames are stacked above the search page. */
  readonly flowDepth: number;
  /** How much room the answer currently occupies. */
  readonly paneMode: 'beside' | 'wide' | 'full';
  /** An answer is present in the preview pane. */
  readonly hasAnswer: boolean;
  /** A scope chip other than `all` is active. */
  readonly hasScope: boolean;
  /** The query, prefix excluded, is non-empty. */
  readonly hasQuery: boolean;
}

export type CmdkEscapeStep =
  | { readonly type: 'stop-voice' }
  | { readonly type: 'close-action-menu' }
  | { readonly type: 'pop-flow' }
  | { readonly type: 'shrink-answer'; readonly to: 'wide' | 'beside' }
  | { readonly type: 'dismiss-answer' }
  | { readonly type: 'clear-query' }
  | { readonly type: 'clear-scope' }
  | { readonly type: 'close' };

/**
 * Resolve one rung.
 *
 * Order, and why each rung sits where it does:
 *
 *  1. **Voice** — the most transient state, and the one where a stray Escape
 *     most obviously means "stop that", not "close everything".
 *  2. **Action menu** — a popover over the list. Closing it reveals what is
 *     underneath, which is the definition of a rung.
 *  3. **Flow frame** — a pushed page. Escape is Back, one frame at a time.
 *  4. **Answer size** — full → wide → beside. Reclaiming room is undoing a
 *     step the user explicitly took.
 *  5. **The answer itself** — dismissing it returns to the preview pane with
 *     the query intact, so the results the answer sat beside are still there.
 *  6. **Query**, then **scope** — the search narrows in that order, so it
 *     unwinds in that order.
 *  7. **Close.**
 *
 * Note what is NOT a rung: the answer has no exit BUTTON. Navigating away is
 * the exit. Rung 5 exists because Escape is a keyboard user's navigation, not
 * because the answer is a mode.
 */
export function resolveEscape(s: CmdkEscapeSnapshot): CmdkEscapeStep {
  if (s.listening) return { type: 'stop-voice' };
  if (s.actionMenuOpen) return { type: 'close-action-menu' };
  if (s.flowDepth > 0) return { type: 'pop-flow' };

  if (s.paneMode === 'full') return { type: 'shrink-answer', to: 'wide' };
  if (s.paneMode === 'wide') return { type: 'shrink-answer', to: 'beside' };
  if (s.hasAnswer) return { type: 'dismiss-answer' };

  if (s.hasQuery) return { type: 'clear-query' };
  if (s.hasScope) return { type: 'clear-scope' };
  return { type: 'close' };
}

/**
 * How many Escapes it takes to close from a given state. Not used at runtime —
 * it exists so the spec can assert the ladder terminates, and terminates
 * quickly, from every reachable combination.
 */
export function escapeDepth(
  s: CmdkEscapeSnapshot,
  apply: (s: CmdkEscapeSnapshot, step: CmdkEscapeStep) => CmdkEscapeSnapshot,
  limit = 12,
): number {
  let current = s;
  for (let i = 0; i < limit; i++) {
    const step = resolveEscape(current);
    if (step.type === 'close') return i + 1;
    current = apply(current, step);
  }
  return Number.POSITIVE_INFINITY;
}
